import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import Table20 from '../../../Components/Table20/Table20';
import { RootState } from '../../../redux/root';
import { calculatePeriod, getDailyInspectionRecords } from './Utils';
import { formatDateTime } from '../../../utils/formatDate';
import { setPageCount, setPageIndex, setRowsCount } from '../../../redux/slices/paginationSlice';
import { AdditionalFieldType } from '../../../Components/Table20/Types';
import { setGlobalSearch } from '../../../redux/slices/filterSlice';
import { setInspectionsTypeData, setInspectorName, setModalOpen, setPermitInspectionsList, setSelectedInspection } from '../../../redux/slices/inspectionsSlice';
import { InspectionItemType } from '../../../modules/clientModule/types';
import InspectionEditor from './InspectionEditor/InspectionEditor';
import { Loader } from '../../../atoms';

type Props = {
  fromDate:any,
  setInspectionCount:Function,
  setAvInspectionCount?:Function,
  openSMS:Function,
  setIsValidForExport:Function,
}
const DailyInspectionRecords:FC<Props> = ({
  fromDate,
  setInspectionCount,
  setAvInspectionCount,
  openSMS,
  setIsValidForExport,
}) => {
  const userId = useSelector((s: RootState) => s.userAuth.info.id);
  const search = useSelector((s: RootState) => s.filters.globalSearch);
  const { pageSize, pageIndex, pageCount, rowsCount } = useSelector((state: RootState) => state.pagination);
  const selectedInspection = useSelector((s:RootState) => s.inspections.selectedInspection);
  const dispatch = useDispatch();
  const _rows = useSelector((s: RootState) => s.inspections.permitInspectionsList);

  const [rows, setRows] = useState([]);
  const [data, setData] = useState([]);
  const [additionalFields, setAdditionalFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const headers = [
    'Permit Number',
    'Inspection ID',
    'Inspection Type',
    'Project Manager',
    'Jurisdiction',
    'AM/PM',
    'Contact',
    'Cell Phone',
    'Contact Phone',
    'Actions',
  ];

  const _fromDate = fromDate || formatDateTime(new Date(), 'MM/dd/yyyy');

  const params = {
    is_deleted: false,
    limit: pageSize,
    offset: pageIndex * pageSize,
    inspector: userId,
    av_operator: userId,
    date_from: _fromDate,
    is_completed: false,
    search,
  };

  useEffect(() => {
    dispatch(setGlobalSearch(undefined));
    dispatch(setPageIndex(0));
  }, []);

  useEffect(() => {
      loadData();
  }, [fromDate, pageSize, pageIndex, search]);

  useEffect(() => {
    setData(_rows);

    // Calculate counts
    // Regular inspections: where user is inspector (not A/V operator)
    const regularInspections = _rows.filter((r: any) => r.inspector === userId && r.avoperator !== userId);
    // A/V inspections: where user is A/V operator
    const avInspections = _rows.filter((r: any) => r.avoperator === userId);

    const regularCount = regularInspections.length;
    const avCount = avInspections.length;
    const totalCount = _rows.length;

    setIsValidForExport(totalCount > 0);
    dispatch(setRowsCount(totalCount));

    setInspectionCount(regularCount);
    if (setAvInspectionCount) {
      setAvInspectionCount(avCount);
    }
    const _af = [];
    const fieldRows = _rows.map((r: any) => {
      const comment = getComment(r);
      const fields = [];

      fields.push({ caption: 'Name', value: `${r.projectName}|${r.permitName}` });
      fields.push({ caption: 'Address', value: r.projectAddress });
      fields.push({ caption: 'Information', value: r.information });
      fields.push({ caption: 'Comment', value: comment });
      if (r.activityTitleName === 'Remote Inspection') {
        const clientOnSite =
          r.is_clientonsite ?? r.isClientonsite ?? r.isClientOnSite ?? r.clientOnSite ?? false;

        const isClientOnSite =
          clientOnSite === true || clientOnSite === 'true' || clientOnSite === 'True' || clientOnSite === 1;

        const avOperatorName = `${r.avoperatorFirstName || ''} ${r.avoperatorLastName || ''}`.trim();
        const inspectorName = `${r.inspectorFirstName || ''} ${r.inspectorLastName || ''}`.trim();

        fields.push({
          caption: 'A/V Operator',
          value: isClientOnSite ? 'Client on Site' : avOperatorName,
        });
        fields.push({
          caption: 'Inspector',
          value: inspectorName,
        });
      }
      _af.push(fields);
      const managerName = r.managerFirstName ? `${r.managerFirstName} ${r.managerLastName}` : '';
      const contactName = r.contactFirstName ? `${r.contactFirstName} ${r.contactLastName}` : '';
      return [
        r.permitNumber,
        r.id,
        r.activityTypeName,
        managerName,
        r.projectJurisdiction,
        calculatePeriod(r),
        contactName,
        r.cellPhone,
        r.phoneNumber,
      ];
    });
    setRows(fieldRows);
    setAdditionalFields(_af);
  }, [_rows]);

  const getComment = input => {
    if (!input.lastComment) {
      return '';
    }
    const { createdAt, text, username } = input.lastComment;
    const date = formatDateTime(new Date(createdAt), 'MM/dd/yyyy');
    const message = text.replace(/<[^>]*>/g, '');
    return `${date}|${username}|${message}`;
  };
  const loadData = async () => {
    const _params = {
      ...params,
      search: params.search === '' ? undefined : params.search,
    };
    setIsLoading(true);
    setIsValidForExport(false);
    try {
      const results: any = await getDailyInspectionRecords(_params);
      dispatch(setInspectionsTypeData(results.result));
      dispatch(setPermitInspectionsList(results.results));
    } finally {
      setIsLoading(false);
    }
  };

  const openEditor = (inspection: InspectionItemType) => {
    dispatch(setSelectedInspection(inspection));
    dispatch(setModalOpen(true));
  };
  return (
    <>
      {isLoading ? (
        <LoaderWrapper>
          <Loader />
          <LoaderText>Loading inspections…</LoaderText>
        </LoaderWrapper>
      ) : (
        <Table20
          headers={headers}
          rows={rows}
          data={data}
          additionalFields={additionalFields}
        // Action1={handleEdit}
          Action1={openEditor}
          Action2={openSMS}
          currentUserId={userId}
        />
      )}
      {selectedInspection.id && <InspectionEditor loadData={loadData} />}

    </>

  );
};

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 16px;
  width: 100%;
`;

const LoaderText = styled.span`
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

export default DailyInspectionRecords;
